<template>
  <div v-if="sent" class="done">
    <p class="done__badge">{{ c.contact.okBadge }}</p>
    <h3 class="done__title">{{ c.contact.okTitle }}</h3>
    <p class="done__text">{{ c.contact.okText }}</p>
    <button class="btn btn--ghost-paper" @click="reset">{{ c.contact.again }}</button>
  </div>

  <form v-else class="form" @submit.prevent="submit" novalidate>
    <p v-if="leadIntent" class="intent full">
      <span>{{ c.plans.intent }}</span>{{ leadIntent }}
      <button type="button" @click="setIntent('')" aria-label="clear">×</button>
    </p>
    <label>
      <span>{{ c.contact.name }} *</span>
      <input v-model.trim="f.name" autocomplete="organization" maxlength="100">
    </label>
    <label>
      <span>{{ c.contact.email }} *</span>
      <input v-model.trim="f.email" type="email" autocomplete="email" maxlength="200">
    </label>
    <label class="full">
      <span>{{ c.contact.phone }} *</span>
      <input v-model.trim="f.phone" maxlength="100">
    </label>
    <label class="full">
      <span>{{ c.contact.message }} *</span>
      <textarea v-model.trim="f.message" rows="5" maxlength="3000"></textarea>
    </label>
    <!-- 蜜罐：对真人隐藏，机器人常会自动填 -->
    <label class="hp" aria-hidden="true">
      <span>Website</span>
      <input v-model="f.website" tabindex="-1" autocomplete="off">
    </label>
    <button class="btn btn--primary full" type="submit" :disabled="sending">
      {{ sending ? c.contact.sending : c.contact.submit + ' →' }}
    </button>
  </form>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { track } from '@/track'
import { leadIntent, setIntent } from '@/leadIntent'
import { content as c, lang, CONTACT_EMAIL } from '@/i18n'
import { toast } from '@/toast'
import { isEmail, isAnyIm } from '@/contact-rules.mjs'

// 表单发到同域的 /api/contact（Cloudflare Pages Function），由它转发到飞书机器人；
// 机器人地址只在服务端环境变量里，前端拿不到
const props = defineProps({ source: { type: String, default: 'home' } })
const route = useRoute()
const f = reactive({ name: '', email: '', phone: '', message: '', website: '' })
const sending = ref(false)
const sent = ref(false)
let openedAt = Date.now()
onMounted(() => { openedAt = Date.now() })

function reset() {
  Object.assign(f, { name: '', email: '', phone: '', message: '', website: '' })
  sent.value = false
  openedAt = Date.now()
}

async function submit() {
  if (!f.name || !f.email || !f.phone || !f.message) return toast(c.value.contact.errRequired, 'error')
  if (!isEmail(f.email)) return toast(c.value.contact.errEmail, 'error')
  if (!isAnyIm(f.phone)) return toast(c.value.contact.errIm, 'error')
  if (sending.value) return
  sending.value = true
  try {
    const res = await fetch('/api/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...f, t: openedAt, lang: lang.value, page: route.path, source: props.source, topic: leadIntent.value }),
    })
    if (res.status === 429) return toast(c.value.contact.errRate + CONTACT_EMAIL, 'error', 8000)
    const out = await res.json().catch(() => ({}))
    if (!res.ok || !out.ok) throw new Error(out.error || 'HTTP ' + res.status)
    sent.value = true
    track('generate_lead', { label: props.source })
  } catch (e) {
    toast(c.value.contact.errSend + CONTACT_EMAIL, 'error', 8000)
  } finally {
    sending.value = false
  }
}
</script>

<style scoped>
.form { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.form label { display: flex; flex-direction: column; gap: 8px; }
.form .full { grid-column: 1 / -1; }
.form span { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; color: var(--paper-muted); text-transform: uppercase; }
.form input, .form textarea {
  width: 100%; background: var(--paper-card); border: 1px solid var(--paper-line); color: var(--paper-text);
  padding: 14px 16px; font-size: 15px; outline: none; transition: border-color .2s, box-shadow .2s; resize: vertical;
}
.form input:focus, .form textarea:focus { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); }
.form .btn:disabled { opacity: .7; cursor: progress; }
.intent { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: var(--accent-soft); border: 1px solid rgba(247,146,16,.35); font-size: 14px; color: var(--paper-text); }
.intent span { font-family: var(--font-mono); font-size: 11px; letter-spacing: .14em; color: var(--accent); text-transform: uppercase; }
.intent button { margin-left: auto; font-size: 18px; color: var(--paper-muted); }
.hp { position: absolute !important; left: -9999px; width: 1px; height: 1px; overflow: hidden; }

.done { text-align: center; padding: 28px 8px; }
.done__badge { display: inline-block; font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; color: var(--ok); border: 1px solid rgba(51,194,127,.5); padding: 4px 10px; }
.done__title { font-family: var(--font-display); font-size: 26px; margin: 18px 0 10px; color: var(--paper-text); }
.done__text { color: var(--paper-muted); margin-bottom: 24px; }
.btn--ghost-paper { border: 1px solid var(--paper-line); color: var(--paper-text); }
.btn--ghost-paper:hover { border-color: var(--accent); color: var(--accent); }
@media (max-width: 600px) { .form { grid-template-columns: 1fr; } }
</style>
